// frontend/src/pages/ReportLayoutPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Upload, Image, Loader2, Save, CheckCircle, XCircle, Building2, MapPin, Phone, Mail, Globe, Hash } from 'lucide-react';

const ReportLayoutPage = ({ apiFetch, onDataChange: notifyAppJs }) => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    
    // Estados para a Logo
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/report-layout-settings');
            setSettings(data || {});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setLogoFile(selectedFile);
            setLogoPreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleUploadLogo = async () => {
        if (!logoFile) return;
        setUploadLoading(true);
        
        // CORREÇÃO: Usando o nome do campo 'logoLaudo' que o backend espera
        const formData = new FormData();
        formData.append('logoLaudo', logoFile);

        try {
            await apiFetch('/report-layout-settings/upload-logo', {
                method: 'POST',
                body: formData,
            });
            setLogoFile(null);
            setLogoPreview(null);
            fetchSettings(); // Recarrega para pegar o novo path
            notifyAppJs();
            alert("Logo do laudo atualizada com sucesso!");
        } catch (err) {
            console.error(err);
            alert("Erro ao salvar logo: " + err.message);
        } finally {
            setUploadLoading(false);
        }
    };

    const handleSubmitText = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        setSaveSuccess(false);

        try {
            // Envia apenas os campos de texto para a rota de settings
            const textData = {
                laudo_empresa_nome: settings.laudo_empresa_nome || '',
                laudo_empresa_cnpj: settings.laudo_empresa_cnpj || '',
                laudo_empresa_ie: settings.laudo_empresa_ie || '',
                laudo_empresa_endereco: settings.laudo_empresa_endereco || '',
                laudo_empresa_telefone: settings.laudo_empresa_telefone || '',
                laudo_empresa_email: settings.laudo_empresa_email || '',
                laudo_empresa_site: settings.laudo_empresa_site || ''
            };

            await apiFetch('/report-layout-settings', {
                method: 'POST',
                body: textData,
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            notifyAppJs();
        } catch (err) {
            alert("Erro ao salvar informações: " + err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-12">
                <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
        );
    }

    // Define a URL da imagem (Prioriza o preview local, depois o caminho do banco)
    const currentLogoUrl = logoPreview || (settings.laudo_logo_path ? `/api/uploads/layout_images/${settings.laudo_logo_path}?t=${new Date().getTime()}` : null);

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <header className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Configurações de Layout (Laudos Técnicos)</h1>
                <p className="text-gray-600 mt-1">Gerencie a identidade visual e os contatos que aparecem nos cabeçalhos dos laudos.</p>
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                
                {/* Coluna da Esquerda: Upload da Logo */}
                <div className="md:col-span-1 p-6 bg-white rounded-lg shadow-md border border-gray-200 space-y-4">
                    <h2 className="text-lg font-semibold text-gray-700 border-b pb-2">Logo do Laudo</h2>
                    
                    <div className="h-40 flex items-center justify-center bg-gray-50 rounded-md border-2 border-dashed border-gray-200 overflow-hidden relative group">
                        {currentLogoUrl ? (
                            <img src={currentLogoUrl} alt="Logo Laudo" className="h-full w-full object-contain p-2" />
                        ) : (
                            <div className="flex flex-col items-center text-gray-400">
                                <Image size={48} />
                                <span className="text-xs mt-2">Sem Logo</span>
                            </div>
                        )}
                    </div>

                    <input 
                        type="file" 
                        accept="image/png, image/jpeg" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        className="hidden" 
                    />

                    <button 
                        type="button"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        <Upload size={16} /> Selecionar Imagem
                    </button>

                    {logoFile && (
                        <button
                            type="button"
                            onClick={handleUploadLogo}
                            disabled={uploadLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 shadow-sm transition-all"
                        >
                            {uploadLoading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} 
                            Salvar Logo no Servidor
                        </button>
                    )}

                    <div className="bg-amber-50 p-3 rounded border border-amber-100">
                        <p className="text-[10px] text-amber-700 leading-tight">
                            * Use imagens PNG com fundo transparente para um melhor acabamento no PDF.
                        </p>
                    </div>
                </div>

                {/* Coluna da Direita: Formulário de Dados */}
                <form onSubmit={handleSubmitText} className="md:col-span-2 p-6 bg-white rounded-lg shadow-md border border-gray-200 space-y-4">
                    <h2 className="text-lg font-semibold text-gray-700 border-b pb-2">Informações da Empresa</h2>
                    
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                <Building2 size={14} className="text-blue-500" /> Nome / Razão Social
                            </label>
                            <input 
                                type="text" 
                                name="laudo_empresa_nome" 
                                value={settings.laudo_empresa_nome || ''} 
                                onChange={handleChange} 
                                className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                                placeholder="Nome exibido no topo do laudo" 
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    <Hash size={14} className="text-blue-500" /> CNPJ
                                </label>
                                <input 
                                    type="text" 
                                    name="laudo_empresa_cnpj" 
                                    value={settings.laudo_empresa_cnpj || ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="00.000.000/0001-00"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    <Hash size={14} className="text-blue-500" /> Insc. Estadual
                                </label>
                                <input 
                                    type="text" 
                                    name="laudo_empresa_ie" 
                                    value={settings.laudo_empresa_ie || ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="Número da IE"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                <MapPin size={14} className="text-blue-500" /> Endereço Completo
                            </label>
                            <input 
                                type="text" 
                                name="laudo_empresa_endereco" 
                                value={settings.laudo_empresa_endereco || ''} 
                                onChange={handleChange} 
                                className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="Rua, Número, Bairro, Cidade - UF" 
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    <Phone size={14} className="text-blue-500" /> Telefone
                                </label>
                                <input 
                                    type="text" 
                                    name="laudo_empresa_telefone" 
                                    value={settings.laudo_empresa_telefone || ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="(11) 99999-9999"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                    <Mail size={14} className="text-blue-500" /> E-mail
                                </label>
                                <input 
                                    type="email" 
                                    name="laudo_empresa_email" 
                                    value={settings.laudo_empresa_email || ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="contato@empresa.com"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-1">
                                <Globe size={14} className="text-blue-500" /> Website
                            </label>
                            <input 
                                type="text" 
                                name="laudo_empresa_site" 
                                value={settings.laudo_empresa_site || ''} 
                                onChange={handleChange} 
                                className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" 
                                placeholder="www.suaempresa.com.br" 
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-4 pt-4 border-t">
                        {saveSuccess && (
                            <div className="flex items-center gap-2 text-green-600 font-bold animate-bounce">
                                <CheckCircle size={18} /> Salvo!
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={saveLoading}
                            className="flex items-center justify-center gap-2 px-8 py-2.5 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 shadow-md transition-all"
                        >
                            {saveLoading ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} 
                            Salvar Informações de Texto
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReportLayoutPage;